import { Stack, Typography, useTheme } from '@mui/material';
import type { DataAccessRule, Role } from '@repo/api-gw-sdk';
import React from 'react';

import OptionMenu, {
  menuItemStyles,
} from '@/components/option-menu/option-menu';
import type { OptionMenuItemType } from '@/components/option-menu/option-menu';
import { Icon } from '@/components/shared/icon';
import type { NamedPermissionDescriptor } from '@/contexts/permissions';

import { getAssignedDataAccessRule } from './utils';

export const PermissionOptionMenu = ({
  icon,
  isPermissionAuthorized,
  permission,
  role,
  onBlocked,
  onAllowed,
  createDataAccessRule,
  assignDataAccessRule,
}: {
  role: Role;
  permission: NamedPermissionDescriptor;
  isPermissionAuthorized: boolean;
  icon: React.ReactNode;
  onBlocked: () => void;
  onAllowed: () => void;
  createDataAccessRule: () => void;
  assignDataAccessRule: (dataAccessRule: DataAccessRule) => void;
}) => {
  const assignedDataAccessRule = getAssignedDataAccessRule(permission, role);

  const allConditions = Object.values(role.dataAccessRules || {});

  const options = usePermissionOptions(
    assignedDataAccessRule,
    isPermissionAuthorized,
    permission.isConditional,
    allConditions
  );

  return (
    <OptionMenu
      iconButtonProps={{ className: 'p-0' }}
      icon={icon}
      options={options}
      onOptionSelected={(option) => {
        if (typeof option !== 'string') {
          const chosenIndex = options.indexOf(option);
          if (chosenIndex === 0) {
            onBlocked();
          } else if (chosenIndex === options.length - 1) {
            onAllowed();
          } else {
            if (option.subMenu?.length || option.divider) {
              return;
            }

            if (option.id === 'create-new-condition') {
              createDataAccessRule();
            } else {
              assignDataAccessRule(
                allConditions.find((x) => x.name === option.id)!
              );
            }
          }
        }
      }}
    />
  );
};

const usePermissionOptions = (
  assignedDataAccessRule: DataAccessRule | undefined,
  isPermissionAuthorized: boolean,
  isConditionalPermission: boolean | undefined,
  allConditions: DataAccessRule[]
): OptionMenuItemType[] => {
  const theme = useTheme();

  const selectedIndex = isConditionalPermission
    ? assignedDataAccessRule
      ? 1
      : isPermissionAuthorized
        ? 2
        : 0
    : isPermissionAuthorized
      ? 1
      : 0;

  return [
    {
      icon: 'material-symbols-close-rounded',
      text: 'Blocked',
      testId: 'blocked-option',
    },
    ...(isConditionalPermission
      ? [
          {
            id: 'create-new-condition',
            icon: 'material-symbols-rule-rounded',
            text: 'Conditional',
            subMenu: allConditions.length
              ? [
                  ...allConditions.map((x, index) => ({
                    id: x.name,
                    menuItemProps: {
                      sx: menuItemStyles(
                        !!(
                          assignedDataAccessRule &&
                          allConditions.indexOf(assignedDataAccessRule) ===
                            index
                        ),
                        theme.palette.mode === 'dark'
                      ),
                    },
                    text: x.name!,
                    testId: `condition-${x.name}`,
                  })),
                  { divider: true, testId: 'divider' },
                  {
                    id: 'create-new-condition',
                    menuItemProps: {
                      sx: menuItemStyles(false, theme.palette.mode === 'dark'),
                    },
                    text: (
                      <Stack alignItems='center' gap='4px' direction='row'>
                        <Icon
                          iconClass={'material-symbols-add-rounded'}
                          sx={{
                            '& i': {
                              fontWeight: '600',
                              color: 'currentColor',
                            },
                          }}
                        />
                        <Typography
                          className='pr-[8px]'
                          color='currentColor'
                          sx={{ fontWeight: '600' }}
                        >
                          Create Access Condition
                        </Typography>
                      </Stack>
                    ),
                    testId: 'create-new-condition',
                  },
                ]
              : undefined,
            testId: 'conditional-option',
          },
        ]
      : []),

    {
      icon: 'material-symbols-check',
      text: 'Allowed',
      testId: 'allowed-option',
    },
  ].map((x, index) => ({
    subMenu: x.subMenu,
    menuItemProps: {
      sx: menuItemStyles(
        selectedIndex === index,
        theme.palette.mode === 'dark'
      ),
    },
    text: x.text,
    icon: (
      <Icon
        iconClass={x.icon}
        sx={{
          '& i': {
            color: 'currentColor',
          },
        }}
      />
    ),
    testId: x.testId,
  }));
};
